import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Button, Input, Popover, Select, Table, Tooltip } from "antd"
import type { ColumnsType } from "antd/es/table"

import DatamapValuesEditor from "features/DataImport/components/DatamapEditor/DatamapValuesEditor.component"
import { removeRow, updateRow } from "features/DataImport/components/DatamapEditor/utils/datamapEdits"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { DATAMAP_TYPES, DatamapDoc, DatamapIssue, DatamapIssueField, DatamapRow, DatamapValue } from "types/datamap.types"

interface DatamapGridProps {
  doc: DatamapDoc
  issues: DatamapIssue[]
  search: string
  onSearch: (search: string) => void
  onChange: (doc: DatamapDoc) => void
}

const PAGE_SIZE = 50
/** What the table needs above and below its scrolling rows: the header row and the pager. */
const TABLE_CHROME_PX = 100
/** The least height the rows get, so a short window still shows a few. */
const MIN_ROWS_PX = 80

/** "1 Yes · 2 No · +3": enough of a row's codes to recognise it. */
export const valuesSummary = (values: DatamapValue[]): string => {
  const shown = values.slice(0, 3).map((value) => `${value.code} ${value.label}`)
  if (values.length > 3) shown.push(`+${values.length - 3}`)
  return shown.join(" · ")
}

/** Rows whose label or variable contains the search text, ignoring case. */
export const filterRows = (rows: DatamapRow[], search: string): DatamapRow[] => {
  const needle = search.trim().toLowerCase()
  if (needle === "") return rows
  return rows.filter((row) => row.label.toLowerCase().includes(needle) || row.variable.toLowerCase().includes(needle))
}

/** Every row of the datamap, editable in place, with each row's problems on the cell they concern. */
const DatamapGrid: React.FC<DatamapGridProps> = ({ doc, issues, search, onSearch, onChange }) => {
  const [page, setPage] = useState(1)
  const rows = useMemo(() => filterRows(doc.rows, search), [doc.rows, search])

  // Back to page 1 on any new search (typed here or set by the editor) and when a row is added
  // first, so the new row is on screen.
  const firstRowId = doc.rows[0]?.id
  useEffect(() => setPage(1), [search, firstRowId])

  // The rows scroll inside the table, in whatever height the editor leaves it, so the pager stays in view.
  const [rowsHeight, setRowsHeight] = useState(MIN_ROWS_PX)
  const measureTableArea = useCallback((node: HTMLDivElement | null) => {
    if (!node) return undefined
    const update = (): void => setRowsHeight(Math.max(MIN_ROWS_PX, node.clientHeight - TABLE_CHROME_PX))
    update()
    // jsdom has no ResizeObserver; the least height does there.
    if (typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const issuesByRow = useMemo(() => {
    const byRow = new Map<string, DatamapIssue[]>()
    for (const issue of issues) byRow.set(issue.rowId, [...(byRow.get(issue.rowId) ?? []), issue])
    return byRow
  }, [issues])

  const messagesFor = (row: DatamapRow, field: DatamapIssueField): string[] =>
    (issuesByRow.get(row.id) ?? []).filter((issue) => issue.field === field).map((issue) => issue.message)

  // Always the same wrapper: if it came and went with the problems, the live validation would
  // remount the cell on the keystroke that changes them, losing focus or closing the codes popover.
  const withMessages = (messages: string[], node: React.ReactElement): React.ReactElement => (
    <Tooltip title={messages.length > 0 ? messages.join(" ") : undefined}>{node}</Tooltip>
  )

  const columns: ColumnsType<DatamapRow> = [
    {
      title: "Label",
      key: "label",
      render: (_value, row) => {
        const messages = messagesFor(row, "label")
        return withMessages(
          messages,
          <Input
            size="small"
            aria-label="Label"
            status={messages.length > 0 ? "error" : undefined}
            value={row.label}
            onChange={(e) => onChange(updateRow(doc, row.id, { label: e.target.value }))}
          />
        )
      },
    },
    {
      title: "Type",
      key: "type",
      width: 130,
      render: (_value, row) => {
        const messages = messagesFor(row, "type")
        return withMessages(
          messages,
          <Select
            size="small"
            className="w-full"
            aria-label="Type"
            status={messages.length > 0 ? "error" : undefined}
            value={row.type}
            options={DATAMAP_TYPES.map((type) => ({ value: type, label: type }))}
            onChange={(type: string) => onChange(updateRow(doc, row.id, { type }))}
          />
        )
      },
    },
    {
      title: "Variable",
      key: "variable",
      width: 180,
      render: (_value, row) => {
        const messages = messagesFor(row, "variable")
        return withMessages(
          messages,
          <Input
            size="small"
            aria-label="Variable"
            status={messages.length > 0 ? "error" : undefined}
            value={row.variable}
            onChange={(e) => onChange(updateRow(doc, row.id, { variable: e.target.value }))}
          />
        )
      },
    },
    {
      title: "Codes",
      key: "values",
      render: (_value, row) => {
        if (row.type !== "CAT" && row.values.length === 0) return <span className="text-xs text-slate-400">None</span>
        const messages = messagesFor(row, "values")
        return withMessages(
          messages,
          <Popover
            trigger="click"
            content={<DatamapValuesEditor values={row.values} onChange={(values) => onChange(updateRow(doc, row.id, { values }))} />}
          >
            <Button size="small" danger={messages.length > 0} aria-label="Codes">
              {row.values.length > 0 ? valuesSummary(row.values) : "Add codes"}
            </Button>
          </Popover>
        )
      },
    },
    {
      title: "",
      key: "actions",
      width: 110,
      render: (_value, row) => (
        <DataQButton size="small" type="danger" onClick={() => onChange(removeRow(doc, row.id))}>
          Remove
        </DataQButton>
      ),
    },
  ]

  return (
    <div className="flex min-h-[240px] flex-1 flex-col gap-2">
      <Input.Search
        allowClear
        className="max-w-sm"
        placeholder="Find a label or variable"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
      />
      <div ref={measureTableArea} className="min-h-0 flex-1 overflow-hidden">
        <Table<DatamapRow>
          size="small"
          rowKey="id"
          dataSource={rows}
          columns={columns}
          scroll={{ y: rowsHeight }}
          pagination={{ current: page, pageSize: PAGE_SIZE, onChange: setPage, showSizeChanger: false }}
        />
      </div>
    </div>
  )
}

export default DatamapGrid
